import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Briefcase, CheckCircle, Clock, FileText, ShieldCheck, UploadCloud, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { expertApplicationService } from '../services/expertApplicationService';

const roleConfig = {
  counselor: {
    title: 'Career Counselor',
    route: '/join/counselor',
    summary: 'Guide students and parents through career discovery, stream selection, aptitude mapping, and higher education planning.',
    eligibility: ['Postgraduate or certified guidance background', 'Experience with students, schools, or families', 'Comfortable with structured reports and career roadmaps'],
    responsibilities: ['Conduct career guidance sessions', 'Interpret MindVrik assessment reports', 'Support stream, subject, and college planning'],
    benefits: ['Verified MindVrik expert listing', 'Structured student referrals', 'Human + AI guidance workflow'],
  },
  'clinical-psychologist': {
    title: 'Clinical Psychologist',
    route: '/join/clinical-psychologist',
    summary: 'Support MindVrik with ethical psychological insight, student well-being guidance, and clinical review when needed.',
    eligibility: ['Relevant psychology qualification', 'Counseling or clinical practice experience', 'Strong consent, privacy, and documentation discipline'],
    responsibilities: ['Review psychological indicators', 'Provide parent/student guidance where appropriate', 'Escalate clinical concerns responsibly'],
    benefits: ['Premium expert ecosystem', 'Privacy-first workflows', 'Collaborative advisory model'],
  },
  'workshop-expert': {
    title: 'Workshop Expert',
    route: '/join/workshop-expert',
    summary: 'Deliver high-quality workshops for schools, parents, and students across career awareness, future skills, and psychological well-being.',
    eligibility: ['Workshop facilitation experience', 'Strong communication skills', 'Subject expertise in education, careers, psychology, or future skills'],
    responsibilities: ['Conduct school and group workshops', 'Create practical learning experiences', 'Coordinate with MindVrik field teams'],
    benefits: ['School program opportunities', 'Scalable workshop formats', 'MindVrik implementation support'],
  },
};

const initialForm = {
  fullName: '',
  email: '',
  mobile: '',
  city: '',
  languages: '',
  qualification: '',
  experience: '',
  specialization: '',
  certifications: '',
  previousOrganizations: '',
  bio: '',
  profileUrl: '',
  resumeFile: null,
  certificatesFile: [],
  availableDays: '',
  availableTimeSlots: '',
  preference: 'Online',
  consent: false,
};

const validate = (form) => {
  const errors = {};
  if (!form.fullName.trim()) errors.fullName = 'Full name is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Enter a valid email.';
  if (!/^\d{10}$/.test(form.mobile.trim())) errors.mobile = 'Enter a valid 10-digit mobile number.';
  if (!form.city.trim()) errors.city = 'City is required.';
  if (!form.languages.trim()) errors.languages = 'Languages are required.';
  if (!form.qualification.trim()) errors.qualification = 'Qualification is required.';
  if (!form.experience.trim()) errors.experience = 'Years of experience is required.';
  if (!form.specialization.trim()) errors.specialization = 'Specialization is required.';
  if (!form.certifications.trim()) errors.certifications = 'Certifications are required.';
  if (!form.previousOrganizations.trim()) errors.previousOrganizations = 'Previous organizations are required.';
  if (form.bio.trim().length < 20) errors.bio = 'Short bio must be at least 20 characters.';
  if (!form.resumeFile) errors.resumeFile = 'Resume is required.';
  if (!form.certificatesFile.length) errors.certificatesFile = 'At least one certificate is required.';
  if (!form.availableDays.trim()) errors.availableDays = 'Available days are required.';
  if (!form.availableTimeSlots.trim()) errors.availableTimeSlots = 'Available time slots are required.';
  if (!form.consent) errors.consent = 'Verification consent is required.';
  return errors;
};

const Field = ({ label, error, as = 'input', children, className = '', ...props }) => (
  <label className={`expert-application-field ${className}`}>
    <span>{label}</span>
    {as === 'textarea' ? <textarea {...props} /> : children || <input {...props} />}
    {error && <em>{error}</em>}
  </label>
);

const formatFileSize = (size) => {
  if (!size) return '0 KB';
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadField = ({ label, description, accept, multiple = false, value, error, onChange }) => {
  const [dragActive, setDragActive] = useState(false);
  const files = multiple ? value : (value ? [value] : []);

  const applyFiles = (fileList) => {
    const selected = Array.from(fileList || []);
    if (!selected.length) return;
    onChange(multiple ? [...files, ...selected] : selected[0]);
  };

  const removeFile = (index) => {
    if (multiple) {
      onChange(files.filter((_, fileIndex) => fileIndex !== index));
      return;
    }
    onChange(null);
  };

  return (
    <div className="expert-upload-field">
      <span>{label}</span>
      <label
        className={`expert-upload-dropzone ${dragActive ? 'active' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragActive(true); }}
        onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
        onDragLeave={(event) => { event.preventDefault(); setDragActive(false); }}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          applyFiles(event.dataTransfer.files);
        }}
      >
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(event) => applyFiles(event.target.files)}
        />
        <UploadCloud size={24} />
        <strong>Drag and drop files here</strong>
        <small>{description}</small>
        <button type="button">Browse files</button>
      </label>
      {!!files.length && (
        <div className="expert-upload-list">
          {files.map((file, index) => (
            <div className="expert-upload-file" key={`${file.name}-${file.size}-${index}`}>
              <FileText size={15} />
              <span>{file.name}</span>
              <small>{formatFileSize(file.size)}</small>
              <button type="button" aria-label={`Remove ${file.name}`} onClick={() => removeFile(index)}>
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <em>{error}</em>}
    </div>
  );
};

const DetailBlock = ({ title, items }) => (
  <div className="expert-application-info">
    <h3>{title}</h3>
    {items.map((item) => (
      <div className="platform-check" key={item}>
        <CheckCircle size={14} />
        {item}
      </div>
    ))}
  </div>
);

const ExpertOnboardingPage = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const config = roleConfig[role] || roleConfig.counselor;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setLoading(true);
    try {
      const savedApplication = await expertApplicationService.submitExpertApplication({
        ...form,
        resumeFile: form.resumeFile ? {
          name: form.resumeFile.name,
          size: form.resumeFile.size,
          type: form.resumeFile.type,
        } : null,
        certificatesFile: form.certificatesFile.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
        })),
        roleApplyingFor: config.title,
      });
      setSubmitted(savedApplication);
    } catch (error) {
      setErrors({ submit: error.message || 'Could not submit your application.' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section className="expert-application-page" aria-label="Expert application success">
        <div className="expert-application-shell">
          <div className="expert-application-success">
            <CheckCircle size={46} />
            <div className="section-label">Application Submitted</div>
            <h1>Pending Review</h1>
            <p>
              Thank you, {submitted.fullName}. Your {submitted.roleApplyingFor} application has been saved for MindVrik review.
            </p>
            <div className="expert-application-id">
              <span>Application ID</span>
              <strong>{submitted.id}</strong>
            </div>
            <div className="dashboard-action-row">
              <button className="btn-primary" onClick={() => navigate('/')}>Back to Home</button>
              <button className="btn-ghost" onClick={() => { setSubmitted(null); setForm(initialForm); }}>Submit Another</button>
            </div>
            <div className="platform-api-note">Your application is securely stored for MindVrik review.</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="expert-application-page" aria-label={`${config.title} onboarding`}>
      <div className="expert-application-shell">
        <button className="checkout-back" type="button" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back to website
        </button>

        <div className="expert-application-layout">
          <aside className="expert-application-copy">
            <div className="section-label">Expert Onboarding</div>
            <h1>Join MindVrik as a {config.title}</h1>
            <p>{config.summary}</p>
            <div className="expert-application-role-grid">
              {Object.entries(roleConfig).map(([key, item]) => (
                <button
                  key={key}
                  type="button"
                  className={key === role ? 'active' : ''}
                  onClick={() => navigate(item.route)}
                >
                  <Briefcase size={16} />
                  <span>{item.title}</span>
                </button>
              ))}
            </div>
            <DetailBlock title="Eligibility" items={config.eligibility} />
            <DetailBlock title="Responsibilities" items={config.responsibilities} />
            <DetailBlock title="Benefits" items={config.benefits} />
            <div className="expert-application-mini-grid">
              <span><ShieldCheck size={16} /> Verification consent</span>
              <span><Clock size={16} /> Pending Review status</span>
              <span><FileText size={16} /> Backend-ready service</span>
            </div>
            <div className="platform-api-note">
              Applications are stored securely and reviewed by the MindVrik team.
            </div>
          </aside>

          <form className="expert-application-card" onSubmit={submit} noValidate>
            <h2>Application Form</h2>
            <p>Complete your details for MindVrik verification and onboarding review.</p>

            <div className="expert-application-section-title">Personal Details</div>
            <div className="expert-application-grid">
              <Field label="Full Name" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} error={errors.fullName} />
              <Field label="Email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} error={errors.email} />
              <Field label="Mobile Number" value={form.mobile} onChange={(e) => update('mobile', e.target.value)} error={errors.mobile} />
              <Field label="City" value={form.city} onChange={(e) => update('city', e.target.value)} error={errors.city} />
              <Field label="Languages" value={form.languages} onChange={(e) => update('languages', e.target.value)} error={errors.languages} />
              <Field label="Role Applying For" value={config.title} readOnly />
            </div>

            <div className="expert-application-section-title">Professional Details</div>
            <div className="expert-application-grid">
              <Field label="Qualification" value={form.qualification} onChange={(e) => update('qualification', e.target.value)} error={errors.qualification} />
              <Field label="Years of Experience" value={form.experience} onChange={(e) => update('experience', e.target.value)} error={errors.experience} />
              <Field label="Specialization" value={form.specialization} onChange={(e) => update('specialization', e.target.value)} error={errors.specialization} />
              <Field label="Certifications" value={form.certifications} onChange={(e) => update('certifications', e.target.value)} error={errors.certifications} />
              <Field label="Previous Organizations" value={form.previousOrganizations} onChange={(e) => update('previousOrganizations', e.target.value)} error={errors.previousOrganizations} />
              <Field label="LinkedIn/Profile URL optional" value={form.profileUrl} onChange={(e) => update('profileUrl', e.target.value)} required={false} />
              <Field className="expert-application-full" label="Short Bio" as="textarea" value={form.bio} onChange={(e) => update('bio', e.target.value)} error={errors.bio} />
            </div>

            <div className="expert-application-section-title">Documents</div>
            <div className="expert-application-grid">
              <UploadField
                label="Resume"
                description="PDF, DOC, or DOCX up to 10 MB"
                accept=".pdf,.doc,.docx"
                value={form.resumeFile}
                error={errors.resumeFile}
                onChange={(file) => update('resumeFile', file)}
              />
              <UploadField
                label="Certifications"
                description="Upload one or more PDF/image certificates"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                multiple
                value={form.certificatesFile}
                error={errors.certificatesFile}
                onChange={(files) => update('certificatesFile', files)}
              />
            </div>

            <div className="expert-application-section-title">Availability</div>
            <div className="expert-application-grid">
              <Field label="Available Days" value={form.availableDays} onChange={(e) => update('availableDays', e.target.value)} error={errors.availableDays} placeholder="Mon, Wed, Sat" />
              <Field label="Available Time Slots" value={form.availableTimeSlots} onChange={(e) => update('availableTimeSlots', e.target.value)} error={errors.availableTimeSlots} placeholder="4 PM - 8 PM" />
              <Field label="Online / Offline / Hybrid preference">
                <select value={form.preference} onChange={(e) => update('preference', e.target.value)}>
                  <option>Online</option>
                  <option>Offline</option>
                  <option>Hybrid</option>
                </select>
              </Field>
            </div>

            <label className="expert-application-consent">
              <input type="checkbox" checked={form.consent} onChange={(e) => update('consent', e.target.checked)} />
              <span>I agree to MindVrik terms, credential verification, and professional background review.</span>
            </label>
            {errors.consent && <div className="auth-message">{errors.consent}</div>}
            {errors.submit && <div className="auth-message">{errors.submit}</div>}

            <button className="btn-primary expert-application-submit" type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Application'} <ArrowRight size={15} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ExpertOnboardingPage;
